// Session notes: local notes per character and for the party, plus the tagged
// DM notes the connector reads from each D&D Beyond sheet's builder page.
// Writing notes back to D&D Beyond is a Phase 3 write path; local notes are
// marked "Local only" until then, exactly as the old console did.
import { useMemo, useState } from "react";
import { newRequestId, request, useBridgeStatus } from "../bridge";
import { useStore } from "../state/store";
import { campaignRoster } from "../state/campaign";

interface LocalNote { id: string; text: string; added: string; field?: string; kind?: string; dmNoteIds?: Record<string, string> }
interface DmNoteRecord { id: string; kind?: string; field?: string; content: string; createdAt?: string; updatedAt?: string; syncStatus?: string }
type NotesMap = Record<string, LocalNote[]>;

const KINDS = ["general", "story", "ruling", "relationship", "standing"];
// The old console stored `added` as display text ("10:16 PM", "From D&D Beyond"); new notes store ISO. Show either.
const when = (added: string) => { const t = Date.parse(added); return Number.isNaN(t) ? added : new Date(t).toLocaleString(); };
// Slash-bookend tagged note block, the format the connector writes into the
// sheet's Background journal fields: //ID ... //ENDID, ID = letters + 3 digits.
const noteIdFor = (characterName: string, taken: Set<string>) => {
  const stem = (characterName.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 8) || "DMNOTE");
  for (let n = 1; n < 1000; n++) { const id = stem + String(n).padStart(3, "0"); if (!taken.has(id)) return id; }
  return stem + Date.now().toString().slice(-3);
};
const blockFor = (id: string, text: string, field: string) => "//" + id + "\n" + (field === "appearance" ? "APPEARANCE\n" : "") + text.trim() + "\n//END" + id;
const FIELDS = ["traits", "ideals", "bonds", "flaws", "appearance", "organizations", "allies", "enemies", "backstory", "other"];

// The write itself takes seconds; the verification re-read is what can run long
// when Chrome has to wake the sheet tab. The connector no longer reloads the
// user's own tabs afterwards (refreshAfter:false) — the verified read is enough.
const NOTES_WRITE_TIMEOUT_MS = 10 * 60 * 1000;

export function SessionNotes() {
  const { layout, update, logActivity } = useStore();
  const bridge = useBridgeStatus();
  const notes = (layout.sessionNotes || {}) as NotesMap;
  // Notes are written about the party at this table. Notes already stored for
  // another campaign are kept untouched and reappear when that campaign opens.
  const roster = campaignRoster(layout);
  const [target, setTarget] = useState<string>("party");
  const [text, setText] = useState("");
  const [kind, setKind] = useState("general");
  const [field, setField] = useState("other");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const selected = roster.find((c) => c.id === target);
  const list = useMemo(() => notes[target] || [], [notes, target]);
  const tagged: DmNoteRecord[] = (selected?.dmNoteRecords as DmNoteRecord[] | undefined) || [];

  function addNote() {
    const body = text.trim();
    if (!body) return;
    const note: LocalNote = { id: newRequestId("note"), text: body, added: new Date().toISOString(), ...(target !== "party" ? { kind, field } : {}) };
    update((l) => ({ ...l, sessionNotes: { ...(l.sessionNotes as NotesMap || {}), [target]: [note, ...((l.sessionNotes as NotesMap || {})[target] || [])] } }));
    logActivity({ text: `Note added for ${selected?.name || "the party"}`, meta: body.slice(0, 80) });
    setText("");
  }
  function removeNote(id: string) {
    update((l) => ({ ...l, sessionNotes: { ...(l.sessionNotes as NotesMap || {}), [target]: ((l.sessionNotes as NotesMap || {})[target] || []).filter((n) => n.id !== id) } }));
  }

  async function refreshTagged() {
    const targets = roster.filter((c) => c.id.startsWith("ddb-")).map((c) => ({ sourceId: c.id.replace(/^ddb-/, ""), name: c.name }));
    if (!targets.length) return;
    setBusy(true); setStatus("Reading tagged DM notes from character sheets…");
    try {
      const result = await request<{ ok: boolean; results?: { sourceId: string; ok: boolean; notes?: DmNoteRecord[]; error?: string }[]; error?: string }>(
        { type: "DM_SCREEN_TAGGED_NOTES_READ_REQUEST", requestId: newRequestId("notes"), targets },
        { resultType: "DM_SCREEN_TAGGED_NOTES_READ_RESULT", timeoutMs: 180000 },
      );
      if (!result.ok && !result.results?.length) throw new Error(result.error || "Note refresh unavailable");
      const bySource = new Map((result.results || []).map((r) => [`ddb-${r.sourceId}`, r]));
      let read = 0, failed = 0;
      update((l) => ({ ...l, characters: l.characters.map((c) => {
        const r = bySource.get(c.id);
        if (!r) return c;
        if (!r.ok) { failed++; return c; }
        read++;
        return { ...c, dmNoteRecords: (r.notes || []).map((n) => ({ ...n, syncStatus: "synced" })) };
      }) }));
      setStatus(`Loaded tagged notes from ${read} sheet${read === 1 ? "" : "s"}${failed ? `; ${failed} could not be read` : ""}. Player-written untagged notes are untouched.`);
      logActivity({ text: `Tagged DM notes refreshed from ${read} sheets`, tone: "green" });
    } catch (e) { setStatus(e instanceof Error ? e.message : "Note refresh failed"); }
    finally { setBusy(false); }
  }

  async function sendToSheet(note: LocalNote) {
    if (!selected || !selected.id.startsWith("ddb-")) return;
    const taken = new Set<string>([...tagged.map((n) => n.id), ...Object.values(notes).flat().flatMap((n) => Object.values(n.dmNoteIds || {}))]);
    const id = noteIdFor(selected.name, taken);
    const field = note.field || "other";
    setBusy(true); setStatus(`Writing the note to ${selected.name}'s sheet and verifying…`);
    try {
      const r = await request<any>({ type: "DM_SCREEN_DM_NOTES_EXPORT_REQUEST", exportId: newRequestId("notes-export"), targets: [{ sourceId: selected.id.replace(/^ddb-/, ""), name: selected.name, mode: "merge", blocks: [{ id, field, block: blockFor(id, note.text, field) }] }], freshVerify: true, refreshAfter: false }, { resultType: "DM_SCREEN_DM_NOTES_EXPORT_RESULT", idKey: "exportId", timeoutMs: NOTES_WRITE_TIMEOUT_MS });
      const result = (r.results || [])[0];
      if (!r.ok && !result?.ok) throw new Error(result?.error || r.error || "D&D Beyond did not confirm the note");
      const record: DmNoteRecord = { id, kind: note.kind || "general", field, content: note.text, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), syncStatus: "synced" };
      update((l) => ({ ...l,
        characters: l.characters.map((c) => (c.id === selected.id ? { ...c, dmNoteRecords: [...((c.dmNoteRecords as DmNoteRecord[]) || []).filter((n) => n.id !== id), record] } : c)),
        sessionNotes: { ...((l.sessionNotes as NotesMap) || {}), [selected.id]: (((l.sessionNotes as NotesMap) || {})[selected.id] || []).map((n) => (n.id === note.id ? { ...n, dmNoteIds: { ...(n.dmNoteIds || {}), [selected.id]: id } } : n)) } }));
      setStatus(`Note written to ${selected.name}'s sheet as ${id} and verified.`);
      logActivity({ text: `DM note sent to D&D Beyond: ${selected.name}`, meta: note.text.slice(0, 80), tone: "green" });
    } catch (e) { setStatus(e instanceof Error ? e.message : "Note export failed"); } finally { setBusy(false); }
  }

  async function removeFromSheet(rec: DmNoteRecord) {
    if (!selected || !selected.id.startsWith("ddb-")) return;
    if (!confirm(`Remove tagged note ${rec.id} from ${selected.name}'s D&D Beyond sheet?`)) return;
    setBusy(true); setStatus(`Removing ${rec.id} from the sheet…`);
    try {
      const r = await request<any>({ type: "DM_SCREEN_DM_NOTES_EXPORT_REQUEST", exportId: newRequestId("notes-remove"), targets: [{ sourceId: selected.id.replace(/^ddb-/, ""), name: selected.name, mode: "remove", blocks: [{ id: rec.id, field: rec.field || "other" }] }], freshVerify: true, refreshAfter: false }, { resultType: "DM_SCREEN_DM_NOTES_EXPORT_RESULT", idKey: "exportId", timeoutMs: NOTES_WRITE_TIMEOUT_MS });
      const result = (r.results || [])[0];
      if (!r.ok && !result?.ok) throw new Error(result?.error || r.error || "D&D Beyond did not confirm the removal");
      update((l) => ({ ...l, characters: l.characters.map((c) => (c.id === selected.id ? { ...c, dmNoteRecords: ((c.dmNoteRecords as DmNoteRecord[]) || []).filter((n) => n.id !== rec.id) } : c)) }));
      setStatus(`${rec.id} removed from the sheet.`);
    } catch (e) { setStatus(e instanceof Error ? e.message : "Note removal failed"); } finally { setBusy(false); }
  }

  return (
    <section className="panel notes">
      <header className="panel-head">
        <div><span className="eyebrow">Session record</span><h1>Session notes</h1><p className="muted">Local notes stay in your profile. Tagged DM notes are read from each sheet's builder page through the connector; sending notes back to D&D Beyond arrives with the export tools.</p></div>
        <div className="actions"><button onClick={refreshTagged} disabled={!bridge.connected || busy}>{busy ? "Reading…" : "↻ Refresh sheet notes"}</button></div>
      </header>
      {status && <p className="muted small">{status}</p>}
      <div className="notes-layout">
        <aside className="note-targets">
          <button className={`rail-item ${target === "party" ? "active" : ""}`} onClick={() => setTarget("party")}>Party <em>{(notes.party || []).length}</em></button>
          {roster.map((c) => <button key={c.id} className={`rail-item ${target === c.id ? "active" : ""}`} onClick={() => setTarget(c.id)}>{c.name} <em>{(notes[c.id] || []).length + ((c.dmNoteRecords as unknown[] | undefined)?.length || 0)}</em></button>)}
        </aside>
        <div className="note-body">
          <form className="note-form" onSubmit={(e) => { e.preventDefault(); addNote(); }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={`New note for ${selected?.name || "the party"}…`} rows={3} />
            <div className="row">
              {target !== "party" && <>
                <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Note kind">{KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select>
                <select value={field} onChange={(e) => setField(e.target.value)} aria-label="Sheet field">{FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}</select>
              </>}
              <span className="spacer" />
              <button type="submit" className="primary" disabled={!text.trim()}>Add note</button>
            </div>
          </form>
          {tagged.length > 0 && (
            <div className="note-group"><h2>From the D&D Beyond sheet <span className="pill">{tagged.length} tagged</span></h2>
              <ul className="note-list">{tagged.map((n) => <li key={n.id}><div className="note-meta">{n.kind || "note"} · {n.field || "—"} · <span className="ok">synced · {n.id}</span> <button className="link small" disabled={!bridge.connected || busy} onClick={() => removeFromSheet(n)}>remove from sheet</button></div><p>{n.content}</p></li>)}</ul>
            </div>
          )}
          <div className="note-group"><h2>Local notes <span className="pill">{list.length}</span></h2>
            {!list.length && <p className="empty">No local notes for {selected?.name || "the party"} yet.</p>}
            <ul className="note-list">{list.map((n) => (
              <li key={n.id}><div className="note-meta">{n.kind ? `${n.kind} · ${n.field || "other"} · ` : ""}{n.dmNoteIds?.[target] ? <span className="ok">on sheet as {n.dmNoteIds[target]}</span> : "Local only"} · {when(n.added)} <button className="link small" onClick={() => removeNote(n.id)}>remove</button>{target !== "party" && !n.dmNoteIds?.[target] && <button className="link small" disabled={!bridge.connected || busy} onClick={() => sendToSheet(n)}>send to D&D Beyond</button>}</div><p>{n.text}</p></li>
            ))}</ul>
          </div>
        </div>
      </div>
    </section>
  );
}
